import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Ban, Check, CircleSlash, Pencil } from 'lucide-react';
import { useState } from 'react';

import { cancelDraftRepairReport } from '#/api/aftersalesMgmt/repairReport/cancelDraft';
import { completeRepairReport } from '#/api/aftersalesMgmt/repairReport/complete';
import { refuseRepairReport } from '#/api/aftersalesMgmt/repairReport/refuse';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  getRepairReportApiErrorMessage,
  isRepairReportVersionConflict,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/apiError';
import {
  canCancelDraftRepairReport,
  canCompleteRepairReport,
  canEditRepairReport,
  canRefuseRepairReport,
} from '#/modules/crmMgmt/aftersales/repairReport/helper/repairReportStatus';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { toast } from '#/utils/toast';
import {
  repairReportDetailQueryKey,
  repairReportListQueryKeyPrefix,
  repairRequestDetailQueryKey,
  repairRequestListQueryKeyPrefix,
} from '#/utils/queryKeys';

function resolveLinkedRepairRequestId(detail) {
  return detail?.repairRequestId
    ?? detail?.repairRequest?.id
    ?? detail?.repairItem?.repairRequestId
    ?? null;
}

const RepairReportCommandBar = ({ detail, onRefetch }) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [cancelDraftOpen, setCancelDraftOpen] = useState(false);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? null;
  const status = detail?.status;

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: repairReportListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: repairReportDetailQueryKey(id) });
    }
    await queryClient.invalidateQueries({ queryKey: repairRequestListQueryKeyPrefix() });
    await queryClient.invalidateQueries({ queryKey: ['erp', 'repairReport', 'forRepairLine'] });
    const repairRequestId = resolveLinkedRepairRequestId(detail);
    if (repairRequestId) {
      await queryClient.invalidateQueries({
        queryKey: repairRequestDetailQueryKey(repairRequestId),
      });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'complete') {
        return completeRepairReport(id, payload);
      }
      if (action === 'refuse') {
        return refuseRepairReport(id, payload);
      }
      if (action === 'cancelDraft') {
        return cancelDraftRepairReport(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'refuse') {
        setRefuseOpen(false);
      }
      if (variables.action === 'cancelDraft') {
        setCancelDraftOpen(false);
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getRepairReportApiErrorMessage(error));
      if (isRepairReportVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  if (!id) {
    return null;
  }

  async function handleComplete() {
    const ok = await confirm({
      title: '完成維修報告',
      message: '完成前請確認維修日、處理方式、檢測結論與檢測項目已填。確定完成？',
      confirmText: '完成',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'complete', payload: { updatedAt } });
  }

  function handleRefuseConfirm(refuseReason) {
    commandMutation.mutate({
      action: 'refuse',
      payload: { updatedAt, refuseReason },
    });
  }

  function handleCancelDraftConfirm(reason) {
    commandMutation.mutate({
      action: 'cancelDraft',
      payload: { updatedAt, reason },
    });
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={cancelDraftOpen}
        onOpenChange={setCancelDraftOpen}
        title="取消維修草稿"
        description="取消後此報告為已取消（不對客公開，內部保留內容與附件），品項回待處理，可再建立新草稿。更正序號或移除品項前須先取消。"
        confirmText="取消草稿"
        reasonLabel="取消原因"
        emptyErrorMessage="請填寫取消原因"
        isPending={isPending}
        onConfirm={handleCancelDraftConfirm}
      />
      <DocumentRejectReasonDialog
        open={refuseOpen}
        onOpenChange={setRefuseOpen}
        title="拒修／作廢"
        description="拒修後為終態，不可重開。請填寫原因。"
        confirmText="拒修"
        reasonLabel="拒修原因"
        emptyErrorMessage="請填寫拒修原因"
        isPending={isPending}
        onConfirm={handleRefuseConfirm}
      />
      {canEditRepairReport(status) ? (
        <Button asChild className="document-detail-edit-action">
          <Link to={ROUTES.repairReportEdit(id)}>
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canCompleteRepairReport(status) ? (
        <Button disabled={isPending} onClick={handleComplete}>
          <Check className="size-3.5" aria-hidden />
          完成
        </Button>
      ) : null}
      {canRefuseRepairReport(status) ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={() => setRefuseOpen(true)}
        >
          <CircleSlash className="size-3.5" aria-hidden />
          拒修
        </Button>
      ) : null}
      {canCancelDraftRepairReport(status) ? (
        <Button
          variant="outline"
          disabled={isPending}
          onClick={() => setCancelDraftOpen(true)}
        >
          <Ban className="size-3.5" aria-hidden />
          取消草稿
        </Button>
      ) : null}
    </>
  );
};

RepairReportCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default RepairReportCommandBar;
